import SmoothScrollbars from '@rio-cloud/rio-uikit/components/smoothScrollbars/SmoothScrollbars';
import Fade from '@rio-cloud/rio-uikit/Fade';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Button from '@rio-cloud/rio-uikit/Button';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { TOUR_HANDLING_DETAILS, TOUR_HANDLING_ROUTE } from '../../common/constants';
import { PageNavigationBar } from '../../components/PageNavigationBar/PageNavigationBar';
import { EditConfirmationDialog } from '../../components/EditConfirmationDialog/EditConfirmationDialog';
import { CancelConfirmationDialog } from '../../components/CancelConfirmationDialog/CancelConfirmationDialog';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { SectionsContainer } from '../../containers/SectionsContainer/SectionsContainer';
import { TourSummary } from '../../containers/TourSummary/TourSummary';
import {
    clearRouteAndMarkers,
    propagateRoute,
    requestBoundingBox,
} from '../../features/livemonitorCommunication/MapRouteRendering';
import { updateItineraryWaypoints } from '../../features/search/updateItineraryWaypoint';
import { editTour } from '../../features/tourEdit/editTour';
import {
    selectDraftExistsFlag,
    selectPageDisplayed,
    selectPastTourFlag,
    selectTourHandlingId,
} from '../../store/app/appSelectors';
import { updatePage, updatePastTourFlag } from '../../store/app/appSlice';
import {
    useGetTourQuery,
    useFetchTourItinerariesQuery,
    useCanceledTourMutation,
    TourStatus,
} from '../../store/facade/facadeApiV3';
import { selectItineraries, selectSelectedItinerary } from '../../store/tour/tourSelectors';
import { NotificationType, showMapNotification } from '../../store/widget/actions/showMapNotification';
import { oneOf } from '../../common/utils';

export const TourDetailsManagerPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const showContent = useAppSelector(selectPageDisplayed) === TOUR_HANDLING_DETAILS;
    const tourId = useAppSelector(selectTourHandlingId);
    const { isFetching: isItineraryFetching } = useFetchTourItinerariesQuery(
        { id: tourId ?? '' },
        { skip: !tourId, refetchOnMountOrArgChange: true }
    );
    const {
        data: tour,
        isSuccess: isTourFulfilled,
        isFetching: isTourFetching,
    } = useGetTourQuery({ id: tourId ?? '' }, { skip: !tourId, refetchOnMountOrArgChange: true });
    const storeItineraries = useAppSelector(selectItineraries);
    // Only use itineraries that belong to the currently requested tour —
    // prevents stale data from a previously selected tour flashing on screen.
    const itinerariesMatchTour = storeItineraries?.[0]?.tourId === tourId;
    const itineraries = itinerariesMatchTour ? storeItineraries : undefined;
    const selectedItinerary = useAppSelector(selectSelectedItinerary);
    const selectedRouteData = itineraries?.at(selectedItinerary ?? 0);

    const pastTourFlag = useAppSelector(selectPastTourFlag);
    const draftExistsFlag = useAppSelector(selectDraftExistsFlag);
    const canEdit =
        !!tourId &&
        !pastTourFlag &&
        isTourFulfilled &&
        (!tour?.status ||
            oneOf(
                tour.status,
                TourStatus.Draft,
                TourStatus.Negotiating,
                TourStatus.Synchronized,
                TourStatus.InProgress,
                TourStatus.WaitingUpdateAcceptance
            ));

    const canCancel =
        !!tourId &&
        !pastTourFlag &&
        isTourFulfilled &&
        !!tour?.status &&
        oneOf(tour.status, TourStatus.Draft, TourStatus.Negotiating, TourStatus.Synchronized);

    const isLoading = isTourFetching || isItineraryFetching;

    const [showEditConfirmation, setShowEditConfirmation] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const [canceledTourRequest] = useCanceledTourMutation();

    // Keep a ref to the latest page value so handleNavigationEnd always reads the
    // up-to-date value even after the Fade exit animation completes (Requirement 5.6)
    const currentPage = useAppSelector(selectPageDisplayed);
    const currentPageRef = useRef(currentPage);
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        if (showContent && itineraries && itineraries.length > 0) {
            // Only update route settings waypoints when this is NOT a past tour (Requirement: past tour
            // selection must not modify the route settings fields)
            if (!pastTourFlag) {
                dispatch(updateItineraryWaypoints);
            }
            dispatch(propagateRoute(itineraries, selectedItinerary ?? 0));
            requestBoundingBox(itineraries);
        }
    }, [itineraries, dispatch, selectedItinerary, pastTourFlag, showContent]);

    const handleNavigationEnd = () => {
        navigate(currentPageRef.current, { replace: false });
    };

    const handleNavigationStart = () => {
        clearRouteAndMarkers();
        dispatch(updatePage(TOUR_HANDLING_ROUTE));
        dispatch(updatePastTourFlag(true));
    };

    const handleEditClick = () => {
        if (draftExistsFlag) {
            setShowEditConfirmation(true);
            return;
        }
        dispatch(editTour);
    };

    const handleConfirmEdit = () => {
        if (!canEdit) {
            return;
        }

        dispatch(editTour);
        setShowEditConfirmation(false);
    };

    const handleCancelEdit = () => {
        setShowEditConfirmation(false);
    };

    const handleCancelTourClick = () => {
        setShowCancelConfirmation(true);
    };

    const handleConfirmCancelTour = async () => {
        if (!tourId || !canCancel) {
            return;
        }
        setShowCancelConfirmation(false);
        try {
            await canceledTourRequest({ id: tourId }).unwrap();
            clearRouteAndMarkers();
            dispatch(updatePage(TOUR_HANDLING_ROUTE));
            const message = intl.formatMessage({ id: 'intl-msg:smartRoute.notification.tourCanceled' });
            showMapNotification(NotificationType.SUCCESS, message);
        } catch (_error) {
            const message = intl.formatMessage({ id: 'intl-msg:smartRoute.notification.tourCancelFailed' });
            showMapNotification(NotificationType.WARNING, message);
        }
    };

    const handleAbortCancellingTour = () => {
        setShowCancelConfirmation(false);
    };

    return (
        <Fade initial animationStyle={Fade.PAGE_BACK} show={showContent} onExitComplete={handleNavigationEnd}>
            <div className='display-flex flex-column height-100vh' data-testid='manager-summary-page'>
                <div data-testid='summary-back-button'>
                    <PageNavigationBar title='intl-msg:smartRoute.details.title' navBack={handleNavigationStart} />
                </div>
                <EditConfirmationDialog
                    show={showEditConfirmation}
                    onConfirm={handleConfirmEdit}
                    onCancel={handleCancelEdit}
                />
                <CancelConfirmationDialog
                    show={showCancelConfirmation}
                    onConfirm={handleConfirmCancelTour}
                    onCancel={handleAbortCancellingTour}
                />
                <SmoothScrollbars>
                    {isLoading ? (
                        <div className='display-flex justify-content-center align-items-center height-100pct'>
                            <Spinner show isFullSized />
                        </div>
                    ) : (
                        itineraries && (
                            <div
                                className='display-flex flex-column gap-25 padding-bottom-15 padding-top-15 padding-x-15'
                                data-testid='tour-handling-layout'
                            >
                                <div className='display-flex justify-content-between align-items-center'>
                                    <Button
                                        data-testid='tour-details-cancel-button'
                                        onClick={handleCancelTourClick}
                                        bsStyle={Button.DANGER}
                                        bsSize={Button.SM}
                                        iconName='rioglyph-trash'
                                        disabled={!canCancel}
                                    >
                                        <FormattedMessage id='intl-msg:smartRoute.tour.cancel.button' />
                                    </Button>
                                    <Button
                                        data-testid='tour-details-edit-button'
                                        onClick={handleEditClick}
                                        bsStyle={Button.PRIMARY}
                                        bsSize={Button.SM}
                                        iconName='rioglyph-pencil'
                                        disabled={!canEdit}
                                    >
                                        <FormattedMessage id='intl-msg:smartRoute.tour.edit.button' />
                                    </Button>
                                </div>

                                <div className='display-flex align-items-start justify-content-between'>
                                    <div className='flex-1'>
                                        <TourSummary itineraries={itineraries} selectedRoute={selectedItinerary ?? 0} />
                                    </div>
                                </div>

                                {selectedRouteData && <SectionsContainer />}
                            </div>
                        )
                    )}
                </SmoothScrollbars>
            </div>
        </Fade>
    );
};
